import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { setSelectedPlayRangeHandle } from 'actions/trimming';

import styles from './styles.module.less';

interface Props {
  x: number;
  y: number;
  color: string;
  hoverColor: string;
  focusRingColor: string;
  width: number;
  height: number;
  curvePx?: number;
  side: 'left' | 'right';
  secs: number;
  active?: boolean;
  onMouseDown?: (evt: React.SyntheticEvent) => void;
  viewboxWidth: number;
}

/**
 * This component makes a handle for the user to grab and drag in order to
 * resize play ranges. The handle looks like:
 *
 *           +---+
 *          /    |
 *         +  +  |
 *         |  |  |
 *         |  |  |
 *         |  |  |
 *         |  |  |
 *         +  +  |
 *          \    |
 *           +---+
 *
 * For the right-hand handle, we use an SVG transform to flip the handle around.
 */

const SectionHandle = ({
  x,
  y,
  active,
  color,
  hoverColor,
  focusRingColor,
  width,
  height,
  curvePx = 2,
  side,
  onMouseDown = () => {
    return;
  },
}: Props): JSX.Element => {
  const ref = useRef<SVGPathElement>();
  const dispatch = useDispatch();
  const [hovered, setHovered] = useState(false);

  const centeredLineHeight = Math.round(height * (3 / 7));
  const path = `
    M${x},${y}
    h-${width - curvePx / 2}
    q-${curvePx},0 -${curvePx},${curvePx}
    v${height - curvePx * 2}
    q0,${curvePx} ${curvePx},${curvePx}
    h${width - curvePx / 2}
    z`;

  const focusPath = `
    M${x},${y}
    h-${width - curvePx / 2}
    q-${curvePx},0 -${curvePx},${curvePx}
    v${height - curvePx * 2}
    q0,${curvePx} ${curvePx},${curvePx}
    h${width - curvePx / 2}`;

  const handleFocus = () => dispatch(setSelectedPlayRangeHandle(side));
  const handleBlur = () => dispatch(setSelectedPlayRangeHandle(null));
  const handleMouseEnter = () => setHovered(true);
  const handleMouseLeave = () => setHovered(false);

  const focused = active || ref.current === document.activeElement;

  const handleLineWidth = Math.max(1.5, Math.floor(width / 5));

  return (
    <g
      className={styles.handle}
      onMouseDown={onMouseDown}
      onClick={e => e.stopPropagation()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <path
        tabIndex={0}
        onFocus={handleFocus}
        onBlur={handleBlur}
        ref={ref}
        fill={focused || hovered ? hoverColor : color}
        d={path}
        transform={
          side === 'right' ? `rotate(180 ${x} ${y + height / 2})` : null
        }
      />
      {focused && (
        <path
          className={styles.focusRing}
          stroke={focusRingColor}
          strokeWidth="4"
          fill="none"
          d={focusPath}
          transform={
            side === 'right' ? `rotate(180 ${x} ${y + height / 2})` : null
          }
        />
      )}
      <line
        x1={side === 'left' ? x - width / 2 : x + width / 2}
        x2={side === 'left' ? x - width / 2 : x + width / 2}
        y1={y + (height - centeredLineHeight) / 2}
        y2={y + (height - centeredLineHeight) / 2 + centeredLineHeight}
        stroke="#FFFFFF"
        fill="#FFFFFF"
        strokeWidth={handleLineWidth}
      />
      {/* Invisible rectangle to extend grab area of handle */}
      <rect
        x={side === 'left' ? x - 20 : x}
        y={y}
        width={width * 2}
        height={height}
        stroke="none"
        fill="white"
        fillOpacity="0"
      />
    </g>
  );
};

export default SectionHandle;
